import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import SiteLayout from "@/components/SiteLayout";
import useScrollReveal from "@/hooks/useScrollReveal";
import HeroBadge from "@/components/HeroBadge";
import { Reveal, RevealGroup, RevealItem } from "@/components/Motion";
import {
  Stethoscope, HeartPulse, Users, Pill, Sparkles, ShieldCheck,
  FileText, Download, ArrowRight, CheckCircle2, Clock3,
} from "lucide-react";

const BENEFITS = [
  { icon: Stethoscope, t: "Unlimited OPD consultations", d: "Consult eligible specialists multiple times during OPD hours — General Physician, Orthopedics, Gynecology, Pediatrics & General Surgery." },
  { icon: HeartPulse, t: "Preventive healthcare", d: "Special rates on diagnostics, pathology, radiology and preventive health-check packages — within hospital infrastructure." },
  { icon: Users, t: "Family healthcare ecosystem", d: "Couple, children and parents on a single membership. Built for the whole household." },
  { icon: Pill, t: "Pharmacy privileges", d: "Eligible discounts on in-house hospital pharmacy purchases — no third-party paperwork." },
  { icon: Sparkles, t: "Wellness initiatives", d: "Access to health awareness programs, community workshops and webinars across the year." },
  { icon: ShieldCheck, t: "Long-term care relationship", d: "Continuous healthcare support under one program." },
];

const NOT_INCLUDED = [
  "Emergency services & emergency-hour admissions",
  "Public holiday consultations (unless approved)",
  "Implants, stents, blood products",
  "Outsourced investigations",
  "Government taxes / GST",
  "Special promotional packages",
];

export default function Benefits() {
  useScrollReveal();
  const [brochure, setBrochure] = useState(null);

  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL;
    axios.get(`${API}/api/brochure`).then((r) => setBrochure(r.data)).catch(() => {});
  }, []);

  return (
    <SiteLayout>
      {/* HERO */}
      <section className="hero-grad relative overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-50 pointer-events-none" />
        <div className="mx-auto max-w-7xl px-6 lg:px-10 pt-16 pb-14 relative">
          <Reveal className="max-w-3xl">
            <HeroBadge>Membership Benefits</HeroBadge>
            <h1 className="mt-5 font-display text-5xl sm:text-6xl lg:text-7xl font-bold text-[#0F2540] leading-[1.04]">
              More access.
              <br /><span className="bg-gradient-to-r from-[#A53A2A] via-[#C89B3C] to-[#A53A2A] bg-clip-text text-transparent">Honest care.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-[#5C6B7A] leading-relaxed">
              NPC is a healthcare privilege program — not insurance. Here's exactly what your card unlocks at Nirmal Super Speciality Hospital, Surat.
            </p>
          </Reveal>
        </div>
      </section>

      {/* BENEFITS GRID */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-16">
        <RevealGroup className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {BENEFITS.map((x, i) => (
            <RevealItem
              key={i}
              className="group bg-white rounded-2xl p-8 border border-[#E5E9F0] shadow-[0_8px_30px_rgba(15,37,64,0.04)] hover:shadow-[0_20px_40px_rgba(15,37,64,0.08)] hover:-translate-y-1 transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]"
              data-testid={`benefit-card-${i}`}
            >
              <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-[#1E4F87] to-[#0F2540] text-white grid place-items-center shadow-lg group-hover:scale-110 transition-transform duration-500">
                <x.icon className="h-6 w-6" />
              </div>
              <h3 className="mt-6 font-display text-xl font-semibold text-[#0F2540]">{x.t}</h3>
              <p className="mt-3 text-sm text-[#5C6B7A] leading-relaxed">{x.d}</p>
            </RevealItem>
          ))}
        </RevealGroup>
      </section>

      {/* BROCHURE DOWNLOAD */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-12">
        <Reveal>
          <div className="rounded-3xl bg-gradient-to-br from-[#F4F7FB] to-[#EAF1FA] border border-[#E5E9F0] p-8 md:p-12">
            <div className="grid lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-7">
                <span className="overline">Detailed Brochure</span>
                <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold text-[#0F2540] leading-tight">
                  Read every detail. <span className="text-[#1E4F87]">Take it home.</span>
                </h2>
                <p className="mt-4 text-[#5C6B7A] text-base sm:text-lg max-w-xl leading-relaxed">
                  Our 3-fold membership brochure (English) — plans, benefits, exclusions, and FAQs in one printable document. Perfect for sharing with family or your HR team.
                </p>
              </div>
              <div className="lg:col-span-5">
                <div className="bg-white rounded-2xl border border-[#E5E9F0] p-6 md:p-7 shadow-[0_12px_40px_rgba(15,37,64,0.08)]" data-testid="brochure-card">
                  <div className="flex items-start gap-4">
                    <div className="h-14 w-14 rounded-2xl bg-[#1E4F87]/8 grid place-items-center flex-shrink-0">
                      <FileText className="h-7 w-7 text-[#1E4F87]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display text-lg font-semibold text-[#0F2540] leading-snug">
                        {brochure?.title || "Membership Brochure (English)"}
                      </div>
                      <div className="text-xs text-[#5C6B7A] mt-1 truncate">
                        PDF · {brochure?.size_label || "~20 MB"}
                      </div>
                    </div>
                  </div>
                  <a
                    href={brochure?.url || "#"}
                    target="_blank" rel="noopener noreferrer"
                    download={brochure?.filename}
                    className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#0F2540] hover:bg-[#1E4F87] text-white px-6 py-3.5 rounded-full font-semibold transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_10px_30px_rgba(15,37,64,0.25)]"
                    data-testid="brochure-download-btn"
                    aria-disabled={!brochure}
                  >
                    <Download className="h-4 w-4" /> Download Brochure
                  </a>
                  <div className="mt-3 text-[11px] text-[#5C6B7A] flex items-center gap-1.5 justify-center">
                    <Clock3 className="h-3 w-3" /> Updated {brochure?.updated_at || "Feb 2026"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* WHAT'S NOT INCLUDED — set honest expectations */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-16">
        <Reveal className="max-w-2xl">
          <span className="overline">Honest disclosure</span>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold text-[#0F2540]">What's <span className="text-[#A53A2A]">not</span> included.</h2>
          <p className="mt-3 text-[#5C6B7A]">
            NPC is a privilege program — not insurance. Some services are intentionally outside its scope:
          </p>
        </Reveal>
        <RevealGroup className="mt-8 grid md:grid-cols-2 gap-3">
          {NOT_INCLUDED.map((x) => (
            <RevealItem key={x}
              className="flex items-start gap-3 bg-white rounded-xl border border-[#E5E9F0] p-4 text-sm text-[#0F2540] hover:border-[#A53A2A]/30 transition-colors">
              <div className="h-5 w-5 rounded-full bg-[#A53A2A]/10 text-[#A53A2A] grid place-items-center flex-shrink-0 mt-0.5 text-xs font-bold">×</div>
              <span className="leading-relaxed">{x}</span>
            </RevealItem>
          ))}
        </RevealGroup>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-16">
        <Reveal>
          <div className="rounded-[2rem] mesh-dark p-10 lg:p-14 relative overflow-hidden">
            <div className="absolute inset-0 dot-grid opacity-15 pointer-events-none" />
            <div className="relative grid lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-7">
                <span className="overline text-[#C89B3C]">Included with every plan</span>
                <h3 className="mt-3 font-display text-3xl lg:text-5xl font-bold text-white leading-tight">A digital card. A QR. A dashboard.</h3>
                <p className="mt-4 text-white/80 text-lg max-w-xl">
                  Activate online in under 5 minutes. Your premium digital membership card is issued instantly with a unique QR — scan it at the Nirmal Hospital reception.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link to="/plans" className="bg-white text-[#0F2540] px-6 py-3 rounded-full font-semibold inline-flex items-center gap-2 hover:bg-[#C89B3C] hover:text-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl" data-testid="benefits-cta-plans">
                    View NPC plans <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
              <div className="lg:col-span-5 grid gap-3">
                {["Unique digital ID + QR card", "Family healthcare ecosystem", "Pharmacy privileges in-house", "Wellness camps across the year", "All eligible specialties covered"].map((x) => (
                  <div key={x} className="flex items-center gap-3 text-white/90">
                    <CheckCircle2 className="h-5 w-5 text-[#C89B3C] flex-shrink-0" /> {x}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </SiteLayout>
  );
}
